import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  styled,
} from "@mui/material";
import { useCallback } from "react";
import colors from "assets/theme-dark/base/colors";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import useSortTable from "../services/useSortTable";

interface TableColumn {
  Header: string;
  accessor: string;
}

interface TableData<T> {
  columns: TableColumn[];
  rows: T[];
}

interface CustomTableProps<T> {
  total: number;
  loading: boolean;
  table: TableData<T>;
  pageSize: number;
  currentPage: number;
  setPage: (page: number) => void;
  setPageSizeData: (size: number) => void;
  selectedRowId: string | null;
  onRowSelect: (id: string) => void;
  identifierColumn: string;
}

const CustomTable = <T extends Record<string, unknown>>({
  total,
  loading,
  table,
  pageSize,
  currentPage,
  setPage,
  setPageSizeData,
  selectedRowId,
  onRowSelect,
  identifierColumn,
}: CustomTableProps<T>) => {
  const { sortedRows, sortField, sortDirection, handleSort } = useSortTable(table.rows, setPage);

  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPageSizeData(parseInt(event.target.value, 10));
      setPage(0);
    },
    [setPage, setPageSizeData]
  );

  const handleRowSelect = useCallback(
    (id: string) => {
      onRowSelect(id);
    },
    [onRowSelect]
  );

  return (
    <TableContainer sx={{ maxHeight: 700 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            {table.columns.map((column) => (
              <TableCell
                key={column.accessor}
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: colors.grey[400],
                  },
                }}
                onClick={() => handleSort(column.accessor)}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {column.Header}
                  {sortField === column.accessor && (
                    <span style={{ marginLeft: 4 }}>
                      {sortDirection === "ASC" ? (
                        <ArrowUpwardIcon fontSize="small" />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" />
                      )}
                    </span>
                  )}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={table.columns.length + 1} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={table.columns.length + 1} align="center">
                No data available
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row, index) => {
              const rowId = row.FX_UNIQUE_ID ? String(row.FX_UNIQUE_ID) : `${index}`;
              return (
                <StyledTableRow key={rowId} selected={selectedRowId === rowId} onClick={() => handleRowSelect(rowId)}>
                  <TableCell padding="checkbox" />
                  {table.columns.map((column) => (
                    <TableCell key={`${rowId}-${column.accessor}`} sx={{ whiteSpace: "nowrap" }}>
                      {String(row[column.accessor] ?? "-")}
                    </TableCell>
                  ))}
                </StyledTableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={currentPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};

const TableContainer = styled("div")`
  overflow-x: auto;
`;

const StyledTableRow = styled(TableRow)<{ selected: boolean }>`
  cursor: pointer;
  pointer-events: auto;
  background-color: ${({ selected }) => (selected ? colors.grey[500] : "transparent")};
  &:hover {
    background-color: ${colors.grey[400]};
  }
`;

export default React.memo(CustomTable);
